import React from "react";
import { motion } from "framer-motion";
import { Home, MapPin, Bell, Lightbulb, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

const GeofenceWidget: React.FC = () => {
    const { t } = useTranslation("nonHealth");
    const [radius, setRadius] = React.useState(200);

    React.useEffect(() => {
        // Simulate radius change or allow user interaction in real app
        // For now, we can just initialize it or leave it as is, 
        // but let's make it stateful to show "dynamic" capability if we were to add controls
        setRadius(200);
    }, []);

    return (
        <motion.div
            key="geofence-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5 }}
        >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-red-100 relative overflow-hidden">
                {/* افکت‌های تزئینی پس‌زمینه */}
                <motion.div
                    className="absolute -top-10 -right-10 w-20 h-20 rounded-full bg-gradient-to-br from-red-200/40 to-red-300/40 blur-xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
                    transition={{
                        duration: 5,
                        repeat: Infinity,
                        repeatType: "mirror",
                    }}
                />
                <motion.div
                    className="absolute -bottom-10 -left-10 w-20 h-20 rounded-full bg-gradient-to-br from-pink-200/40 to-pink-300/40 blur-xl"
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.7, 0.5] }}
                    transition={{
                        duration: 5,
                        repeat: Infinity,
                        repeatType: "mirror",
                        delay: 1,
                    }}
                />

                {/* هدر ویجت */}
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                        <motion.span
                            className="text-red-600 text-xl"
                            animate={{ rotate: [0, 10, 0] }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                repeatType: "mirror",
                                delay: 1,
                            }}
                        >
                            <Home className="w-6 h-6" />
                        </motion.span>
                        <h5 className="text-red-800 font-medium">{t("widgets.geofence.title")}</h5>
                    </div>
                    <motion.div
                        className="flex items-center gap-1 bg-gradient-to-r from-red-500 to-rose-500 text-white px-2 py-1 rounded-full text-xs font-medium"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        <motion.span className="inline-block w-2 h-2 rounded-full bg-white ml-1" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                        {t("widgets.geofence.active")}
                    </motion.div>
                </div>

                {/* نمایشگر محدوده */}
                <motion.div
                    className="relative h-48 mb-4 overflow-hidden rounded-xl bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center shadow-lg border border-red-200 hover:shadow-xl transition-shadow duration-300"
                    whileHover={{ scale: 1.02 }}
                >
                    {/* نقشه استایلیزه */}
                    <motion.div className="absolute inset-0 w-full h-full opacity-25">
                        <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 border-t border-l border-gray-300/70" />
                        <div className="absolute top-1/4 right-1/4 w-1/2 h-1/2 border-t border-r border-gray-300/70" />
                        <div className="absolute bottom-1/4 left-1/4 w-1/2 h-1/2 border-b border-l border-gray-300/70" />
                        <div className="absolute bottom-1/4 right-1/4 w-1/2 h-1/2 border-b border-r border-gray-300/70" />
                    </motion.div>

                    {/* دایره محدوده */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <motion.div
                            className="absolute w-36 h-36 rounded-full border-2 border-red-300"
                            animate={{
                                scale: [1, 1.15, 1],
                                opacity: [0.5, 0.2, 0.5],
                            }}
                            transition={{
                                duration: 3.5,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        />
                        <motion.div
                            className="absolute w-32 h-32 rounded-full border-2 border-red-400"
                            animate={{
                                scale: [1.05, 0.95, 1.05],
                                opacity: [0.7, 0.4, 0.7],
                            }}
                            transition={{
                                duration: 3.5,
                                repeat: Infinity,
                                delay: 0.7,
                                ease: "easeInOut",
                            }}
                        />
                        <motion.div className="w-28 h-28 border-2 border-red-500 rounded-full flex items-center justify-center bg-white/60 backdrop-blur-md shadow-lg">
                            <motion.div
                                animate={{ scale: [1, 1.03, 1] }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                            >
                                <Home className="w-8 h-8 text-red-500" />
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* نشانگر موقعیت کودک */}
                    <motion.div
                        className="absolute w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center cursor-pointer group"
                        style={{ top: "30%", left: "40%" }}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        animate={{
                            scale: [1, 1.1, 1],
                            boxShadow: ["0 0 0 0px rgba(16, 185, 129, 0.6)", "0 0 0 8px rgba(16, 185, 129, 0)", "0 0 0 0px rgba(16, 185, 129, 0.6)"],
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <motion.div className="w-2 h-2 bg-white rounded-full" animate={{ opacity: [1, 0.6, 1] }} transition={{ duration: 1.2, repeat: Infinity }} />
                        <div className="absolute -top-7 -right-4 bg-gray-800/90 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">{t("widgets.geofence.child")}</div>
                    </motion.div>

                    {/* نشانگر وضعیت */}
                    <motion.div
                        className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-3 py-1 rounded-full shadow-md text-xs flex items-center gap-2 border border-gray-100"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        <motion.span className="w-2 h-2 rounded-full bg-green-500" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                        <span className="text-gray-700">
                            {t("widgets.geofence.status")} <span className="font-medium text-green-600">{t("widgets.geofence.insideRange")}</span>
                        </span>
                    </motion.div>
                </motion.div>

                {/* میکرو ویجت‌ها */}
                <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                    <motion.div
                        className="bg-gradient-to-br from-red-50 to-rose-50 p-3 rounded-lg shadow-sm border border-red-200 relative overflow-hidden"
                        whileHover={{ scale: 1.02, y: -2 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                        <div className="absolute -right-4 -bottom-4 w-12 h-12 rounded-full bg-red-100 opacity-50" />
                        <div className="text-red-700 font-medium mb-1 flex items-center gap-1">
                            <span className="text-red-500">
                                <MapPin className="w-4 h-4" />
                            </span>
                            {t("widgets.geofence.radius")}
                        </div>
                        <div className="flex justify-between items-end">
                            <div className="text-gray-700 font-bold text-base">{radius}</div>
                            <div className="text-gray-500 text-xs">{t("widgets.geofence.meters")}</div>
                        </div>
                        <motion.div className="w-full bg-red-200 h-1 mt-1.5 rounded-full overflow-hidden" whileHover={{ height: "6px" }}>
                            <motion.div className="h-full bg-gradient-to-r from-red-400 to-rose-500 rounded-full" style={{ width: "40%" }} whileHover={{ width: "60%" }} />
                        </motion.div>
                    </motion.div>

                    <motion.div
                        className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg shadow-sm border border-green-200 relative overflow-hidden"
                        whileHover={{ scale: 1.02, y: -2 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                        <div className="absolute -right-4 -bottom-4 w-12 h-12 rounded-full bg-green-100 opacity-50" />
                        <div className="text-green-700 font-medium mb-1 flex items-center gap-1">
                            <span className="text-green-500">
                                <Bell className="w-4 h-4" />
                            </span>
                            {t("widgets.geofence.alertStatus")}
                        </div>
                        <div className="flex justify-between items-center">
                            <motion.div className="relative w-8 h-4 bg-gray-200 rounded-full flex items-center px-0.5" whileHover={{ scale: 1.1 }}>
                                <motion.div className="w-3 h-3 bg-white rounded-full shadow-sm" animate={{ x: 0 }} transition={{ type: "spring", stiffness: 500, damping: 30 }} />
                            </motion.div>
                            <div className="text-gray-600 font-medium">{t("widgets.geofence.inactive")}</div>
                        </div>
                    </motion.div>
                </div>

                {/* پیام اطلاع‌رسانی */}
                <motion.div
                    className="bg-blue-50 border border-blue-200 text-blue-700 text-xs p-2.5 rounded-lg flex items-center mb-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                >
                    <motion.span
                        className="text-lg ml-2"
                        animate={{ rotate: [0, 10, 0] }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "mirror",
                            delay: 2,
                        }}
                    >
                        <Lightbulb className="w-5 h-5" />
                    </motion.span>
                    <span>{t("widgets.geofence.notification")}</span>
                </motion.div>

                {/* دکمه اصلی */}
                <motion.button
                    className="w-full bg-gradient-to-r from-red-500 to-rose-600 text-white py-2.5 rounded-lg text-sm font-medium shadow-md relative overflow-hidden group"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                    <motion.span className="absolute inset-0 w-full h-full bg-gradient-to-r from-rose-400 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <motion.span className="relative flex items-center justify-center gap-2" whileHover={{ letterSpacing: "0.5px" }}>
                        <motion.span
                            animate={{ rotate: [0, 15, 0] }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                repeatType: "mirror",
                                delay: 1,
                            }}
                        >
                            <Pencil className="w-4 h-4" />
                        </motion.span>
                        {t("widgets.geofence.edit")}
                    </motion.span>
                </motion.button>
            </div>
        </motion.div>
    );
};

export default GeofenceWidget;
